// src/pages/CAAutomationControl.tsx
import React from "react";
import type { ColumnsType, TableProps } from "antd/es/table";
import {
  Card,
  Space,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Dropdown,
  type MenuProps,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useDataProvider } from "@refinedev/core";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { SnackbarProvider, useSnackbar } from "notistack";
import { downloadFile } from "../utils/downloadFile";

const CA_SCHEMA = "ca";

type ExportFormat = "pem" | "chain_pem" | "der" | "pfx" | "p7b";

type CertAutomation = {
  id: string;
  certificate_id: string;
  target_type_id: number | null;
  hostname: string | null;
  port: number | null;
  destination_path: string | null;

  output_format: string;
  include_chain: boolean;
  include_key: boolean;

  enabled: boolean;
  status_id?: number | null;
  last_run_at?: string | null;
  updated_at?: string | null;

  cron_expr?: string | null;
  // other columns exist but we only type what we use explicitly
};

type Certificate = {
  id: string;
  common_name: string;
};

type TargetType = {
  id: number;
  code: string;
};

type AutomationCredential = {
  id: string;
  cred_name: string;
};

type AutomationJksCredential = {
  id: string;
  cred_name: string;
};

type ResizableTitleProps = React.HTMLAttributes<HTMLTableCellElement> & {
  width?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
};

const ResizableTitle: React.FC<ResizableTitleProps> = (props) => {
  const { onResize, width, style, ...rest } = props;
  if (!width) return <th {...rest} />;

  return (
    <Resizable
      width={width}
      height={0}
      onResize={onResize!}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...rest} style={{ ...style, whiteSpace: "nowrap" }} />
    </Resizable>
  );
};

function useResizableColumns<T extends { key?: React.Key }>(
  base: ColumnsType<T>,
  initial: number[],
): { columns: ColumnsType<T>; components: TableProps<T>["components"] } {
  const [cols, setCols] = React.useState(
    base.map((c, i) => ({
      ...c,
      width: initial[i] ?? (c.width as number) ?? 120,
    })),
  );

  const handleResize =
    (index: number) =>
    (_: any, { size }: { size: { width: number } }) => {
      setCols((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], width: size.width };
        return next;
      });
    };

  const columns = cols.map((col, index) => ({
    ...col,
    onHeaderCell: (column: any) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  const components: TableProps<T>["components"] = {
    header: { cell: ResizableTitle },
  };

  return { columns, components };
}

const formatNeedsPassword = (
  format: ExportFormat,
  row: CertAutomation,
): boolean => {
  if (format === "pfx" || format === "p7b") return true;
  if (format === "pem" || format === "der") return !!row.include_key;
  if (format === "chain_pem") return false;
  return false;
};

const mimeFromFormat = (format: ExportFormat): string => {
  switch (format) {
    case "pem":
    case "chain_pem":
      return "application/x-pem-file";
    case "der":
      return "application/x-x509-ca-cert";
    case "pfx":
      return "application/x-pkcs12";
    case "p7b":
      return "application/x-pkcs7-certificates";
    default:
      return "application/octet-stream";
  }
};

const extensionFromFormat = (format: ExportFormat): string => {
  switch (format) {
    case "pem":
      return "pem";
    case "chain_pem":
      return "chain.pem";
    case "der":
      return "der";
    case "pfx":
      return "pfx";
    case "p7b":
      return "p7b";
    default:
      return "bin";
  }
};

const filenameFromRowAndFormat = (
  row: CertAutomation,
  certs: Certificate[],
  format: ExportFormat,
): string => {
  const cert = certs.find((c) => c.id === row.certificate_id);
  const base =
    row.hostname ||
    cert?.common_name ||
    row.destination_path ||
    `automation-${row.id}`;
  const ext = extensionFromFormat(format);
  if (ext.includes(".")) {
    return `${base}.${ext.split(".").slice(-1)[0]}`;
  }
  return `${base}.${ext}`;
};

/* ---------- INNER PAGE ---------- */
const CAAutomationControlInner: React.FC = () => {
  const getDp = useDataProvider();
  const dp = getDp();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [search, setSearch] = React.useState("");
  const [rows, setRows] = React.useState<CertAutomation[]>([]);

  const [certs, setCerts] = React.useState<Certificate[]>([]);
  const [targetTypes, setTargetTypes] = React.useState<TargetType[]>([]);
  const [authCreds, setAuthCreds] = React.useState<AutomationCredential[]>([]);
  const [jksCreds, setJksCreds] = React.useState<AutomationJksCredential[]>([]);

  // client-side pagination
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);

  // form modal
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CertAutomation | null>(null);
  const [form] = Form.useForm();

  // download/password modal
  const [downloadOpen, setDownloadOpen] = React.useState(false);
  const [downloadPassword, setDownloadPassword] = React.useState("");
  const [downloadSubmitting, setDownloadSubmitting] = React.useState(false);
  const [downloadRow, setDownloadRow] = React.useState<CertAutomation | null>(
    null,
  );
  const [downloadFormat, setDownloadFormat] =
    React.useState<ExportFormat | null>(null);

  const loadLookups = React.useCallback(async () => {
    const api = dp;
    if (!api) return;
    try {
      const [certRes, targetRes, credRes, jksRes] = await Promise.all([
        api.getList<Certificate>({
          resource: "certificates",
          pagination: { current: 1, pageSize: 1000 },
          sorters: [{ field: "common_name", order: "asc" }],
          meta: { schema: CA_SCHEMA },
        }),
        api.getList<TargetType>({
          resource: "automation_target_types",
          pagination: { current: 1, pageSize: 100 },
          sorters: [{ field: "code", order: "asc" }],
          meta: { schema: CA_SCHEMA },
        }),
        api.getList<AutomationCredential>({
          resource: "automation_credentials",
          pagination: { current: 1, pageSize: 1000 },
          sorters: [{ field: "cred_name", order: "asc" }],
          meta: { schema: CA_SCHEMA },
        }),
        api.getList<AutomationJksCredential>({
          resource: "automation_jks_credentials",
          pagination: { current: 1, pageSize: 1000 },
          sorters: [{ field: "cred_name", order: "asc" }],
          meta: { schema: CA_SCHEMA },
        }),
      ]);

      setCerts(certRes.data);
      setTargetTypes(targetRes.data);
      setAuthCreds(credRes.data);
      setJksCreds(jksRes.data);
    } catch (err: any) {
      enqueueSnackbar(
        `Failed to load lookups: ${err?.message || "Unknown error"}`,
        { variant: "error" },
      );
    }
  }, [dp, enqueueSnackbar]);

  const loadAll = React.useCallback(async () => {
    if (!dp) return;
    setLoading(true);
    try {
      const res = await dp.getList<CertAutomation>({
        resource: "certificate_automations",
        pagination: { current: 1, pageSize: 1000 },
        sorters: [{ field: "updated_at", order: "desc" }],
        meta: { schema: CA_SCHEMA },
      });
      setRows(res.data);
    } catch (err: any) {
      enqueueSnackbar(
        `Failed to load automations: ${err?.message || "Unknown error"}`,
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  }, [dp, enqueueSnackbar]);

  React.useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ------------ Form actions ------------ */
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      certificate_id: undefined,
      output_format: "pem",
      include_chain: true,
      include_key: true,
      enabled: true,
    });
    setModalOpen(true);
  };

  const openEdit = (row: CertAutomation) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      ...row,
    });
    setModalOpen(true);
  };

  const handleFormFinish = async (values: any) => {
    if (!dp) return;
    setSaving(true);
    try {
      if (editing) {
        await dp.update({
          resource: "certificate_automations",
          id: editing.id,
          variables: values,
          meta: { schema: CA_SCHEMA },
        });
        enqueueSnackbar("Automation updated", { variant: "success" });
      } else {
        await dp.create({
          resource: "certificate_automations",
          variables: values,
          meta: { schema: CA_SCHEMA },
        });
        enqueueSnackbar("Automation created", { variant: "success" });
      }
      setModalOpen(false);
      setEditing(null);
      await loadAll();
    } catch (err: any) {
      enqueueSnackbar(`Save failed: ${err?.message || "Unknown error"}`, {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: CertAutomation) => {
    if (!dp) return;
    Modal.confirm({
      title: "Delete automation?",
      content: "This cannot be undone.",
      okType: "danger",
      onOk: async () => {
        try {
          await dp.deleteOne({
            resource: "certificate_automations",
            id: row.id,
            meta: { schema: CA_SCHEMA },
          });
          enqueueSnackbar("Deleted", { variant: "info" });
          await loadAll();
        } catch (err: any) {
          enqueueSnackbar(`Delete failed: ${err?.message || "Unknown error"}`, {
            variant: "error",
          });
        }
      },
    });
  };

  const handleRun = async (row: CertAutomation) => {
    if (!dp) return;
    const masterPassword =
      window.prompt(
        "Master password for remote push (leave blank if not required):",
      ) || null;

    try {
      const url = `${dp.getApiUrl()}/rpc/run_certificate_automations`;
      await dp.custom({
        url,
        method: "POST",
        data: {
          p_automation_id: row.id,
          p_master_password: masterPassword,
        },
        resource: "certificate_automations",
        meta: { schema: CA_SCHEMA },
      });
      enqueueSnackbar("Automation run triggered", { variant: "info" });
    } catch (err: any) {
      enqueueSnackbar(`Run failed: ${err?.message || "Unknown error"}`, {
        variant: "error",
      });
    }
  };

  const runExport = async (
    row: CertAutomation,
    format: ExportFormat,
    password: string | null,
  ) => {
    if (!dp) return;
    if (!row.certificate_id) {
      throw new Error("No certificate_id on automation row");
    }
    const url = `${dp.getApiUrl()}/rpc/export_certificate`;

    const res = await dp.custom({
      url,
      method: "POST",
      data: {
        p_cert_id: row.certificate_id,
        p_format: format,
        p_output_password: password,
      },
      resource: "certificate_automations",
      meta: { schema: CA_SCHEMA },
    });

    const data = res.data;
    let blob: Blob;

    if (data instanceof Blob) {
      blob = data;
    } else if (typeof data === "string") {
      blob = new Blob([data], { type: mimeFromFormat(format) });
    } else if (data && typeof data === "object") {
      // Try to grab first string field (e.g. { export_certificate: "..." })
      const val = Object.values(data as any)[0];
      if (typeof val === "string") {
        blob = new Blob([val], { type: mimeFromFormat(format) });
      } else {
        const jsonString = JSON.stringify(data, null, 2);
        blob = new Blob([jsonString], { type: "application/json" });
      }
    } else {
      throw new Error("Unexpected export response");
    }

    const filename = filenameFromRowAndFormat(row, certs, format);
    downloadFile(blob, filename);
  };

  const openDownloadForFormat = async (
    row: CertAutomation,
    format: ExportFormat,
  ) => {
    const needsPwd = formatNeedsPassword(format, row);

    if (!needsPwd) {
      try {
        setDownloadSubmitting(true);
        await runExport(row, format, null);
        enqueueSnackbar(`Downloaded ${format.toUpperCase()} successfully`, {
          variant: "success",
        });
      } catch (err: any) {
        enqueueSnackbar(`Download failed: ${err?.message || "Unknown error"}`, {
          variant: "error",
        });
      } finally {
        setDownloadSubmitting(false);
      }
      return;
    }

    setDownloadRow(row);
    setDownloadFormat(format);
    setDownloadPassword("");
    setDownloadOpen(true);
  };

  const handleDownloadConfirm = async () => {
    if (!downloadRow || !downloadFormat) return;
    if (!downloadPassword) {
      enqueueSnackbar("Password is required for this format", {
        variant: "warning",
      });
      return;
    }
    setDownloadSubmitting(true);
    try {
      await runExport(downloadRow, downloadFormat, downloadPassword);
      enqueueSnackbar(
        `Downloaded ${downloadFormat.toUpperCase()} successfully`,
        { variant: "success" },
      );
      setDownloadOpen(false);
      setDownloadRow(null);
      setDownloadFormat(null);
      setDownloadPassword("");
    } catch (err: any) {
      enqueueSnackbar(`Download failed: ${err?.message || "Unknown error"}`, {
        variant: "error",
      });
    } finally {
      setDownloadSubmitting(false);
    }
  };

  const handleDownloadCancel = () => {
    setDownloadOpen(false);
    setDownloadRow(null);
    setDownloadFormat(null);
    setDownloadPassword("");
  };

  /* ------------ Table / search ------------ */
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const fields = [
        r.hostname || "",
        r.destination_path || "",
        String(r.port ?? ""),
      ];
      return fields.some((f) => f.toLowerCase().includes(q));
    });
  }, [rows, search]);

  const paged = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const menuItemsForRow = (row: CertAutomation): MenuProps["items"] => [
    {
      key: "run",
      icon: <PlayCircleOutlined />,
      label: "Run now",
    },
    { type: "divider" },
    {
      key: "download-pem",
      icon: <DownloadOutlined />,
      label: "Download PEM",
    },
    {
      key: "download-chain",
      icon: <DownloadOutlined />,
      label: "Download chain (PEM)",
    },
    {
      key: "download-der",
      icon: <DownloadOutlined />,
      label: "Download DER",
    },
    {
      key: "download-pfx",
      icon: <DownloadOutlined />,
      label: "Download PFX",
    },
    {
      key: "download-p7b",
      icon: <DownloadOutlined />,
      label: "Download P7B",
    },
    { type: "divider" },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
    },
  ];

  const handleRowMenuClick = (key: string, row: CertAutomation) => {
    switch (key) {
      case "run":
        void handleRun(row);
        break;
      case "download-pem":
        void openDownloadForFormat(row, "pem");
        break;
      case "download-chain":
        void openDownloadForFormat(row, "chain_pem");
        break;
      case "download-der":
        void openDownloadForFormat(row, "der");
        break;
      case "download-pfx":
        void openDownloadForFormat(row, "pfx");
        break;
      case "download-p7b":
        void openDownloadForFormat(row, "p7b");
        break;
      case "edit":
        openEdit(row);
        break;
      case "delete":
        void handleDelete(row);
        break;
      default:
        break;
    }
  };

  const columnsBase: ColumnsType<CertAutomation> = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: 60,
      fixed: "left",
      render: (_value, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: menuItemsForRow(record),
            onClick: ({ key }) => handleRowMenuClick(key as string, record),
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: "Certificate",
      dataIndex: "certificate_id",
      key: "certificate_id",
      render: (value: string) => {
        const c = certs.find((cert) => cert.id === value);
        return c ? c.common_name : value;
      },
    },
    {
      title: "Target",
      dataIndex: "target_type_id",
      key: "target_type_id",
      render: (value: number | null) => {
        const tt = targetTypes.find((t) => t.id === value);
        return tt ? tt.code : value;
      },
    },
    {
      title: "Host",
      dataIndex: "hostname",
      key: "hostname",
    },
    {
      title: "Port",
      dataIndex: "port",
      key: "port",
      width: 80,
    },
    {
      title: "Destination",
      dataIndex: "destination_path",
      key: "destination_path",
    },
    {
      title: "Format",
      dataIndex: "output_format",
      key: "output_format",
      width: 100,
    },
    {
      title: "Chain",
      dataIndex: "include_chain",
      key: "include_chain",
      width: 80,
      render: (v: boolean) => (
        <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Key",
      dataIndex: "include_key",
      key: "include_key",
      width: 80,
      render: (v: boolean) => (
        <Tag color={v ? "volcano" : "default"}>{v ? "With key" : "No key"}</Tag>
      ),
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      render: (v: boolean) => (
        <Tag color={v ? "green" : "default"}>{v ? "Enabled" : "Disabled"}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status_id",
      key: "status_id",
      width: 80,
    },
    {
      title: "Last run",
      dataIndex: "last_run_at",
      key: "last_run_at",
      width: 170,
      render: (v?: string | null) => (v ? new Date(v).toLocaleString() : ""),
    },
  ];

  const { columns, components } = useResizableColumns(
    columnsBase,
    [60, 220, 120, 160, 80, 260, 100, 80, 80, 90, 80, 170],
  );

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadAll()}
            loading={loading}
          >
            Reload
          </Button>
          <Input.Search
            placeholder="Search hostname / destination / port"
            allowClear
            style={{ width: 320 }}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </Space>

        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New Automation
        </Button>
      </Space>

      <Card>
        <Table<CertAutomation>
          dataSource={paged}
          rowKey="id"
          size="small"
          loading={loading || downloadSubmitting}
          columns={columns}
          components={components}
          pagination={{
            current: page,
            pageSize,
            total: filtered.length,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create / Edit modal */}
      <Modal
        open={modalOpen}
        title={editing ? "Edit Automation" : "New Automation"}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={900}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleFormFinish}>
          <Space wrap style={{ width: "100%" }} size="middle">
            <Form.Item
              name="certificate_id"
              label="Certificate"
              rules={[{ required: true }]}
              style={{ minWidth: 260, flex: 1 }}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={certs.map((c) => ({
                  value: c.id,
                  label: c.common_name,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="output_format"
              label="Output format"
              style={{ width: 200 }}
            >
              <Select
                options={[
                  { value: "pem", label: "PEM (files)" },
                  { value: "pkcs12", label: "PKCS#12 (PFX/JKS)" },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="target_type_id"
              label="Target type"
              style={{ width: 200 }}
            >
              <Select
                allowClear
                options={targetTypes.map((t) => ({
                  value: t.id,
                  label: t.code,
                }))}
              />
            </Form.Item>

            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item name="cron_expr" label="Cron (optional)">
              <Input placeholder="*/5 * * * *" />
            </Form.Item>
          </Space>

          <Space wrap style={{ width: "100%" }} size="middle">
            <Form.Item name="hostname" label="Hostname">
              <Input />
            </Form.Item>
            <Form.Item name="port" label="Port">
              <InputNumber style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="destination_path" label="Destination path">
              <Input />
            </Form.Item>
          </Space>

          <Space wrap style={{ width: "100%" }} size="middle">
            <Form.Item
              name="include_chain"
              label="Include chain"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="include_key"
              label="Include private key"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>

          <Space wrap style={{ width: "100%" }} size="middle">
            <Form.Item name="auth_cred_id" label="Auth credential">
              <Select
                allowClear
                options={authCreds.map((c) => ({
                  value: c.id,
                  label: c.cred_name,
                }))}
              />
            </Form.Item>
            <Form.Item name="jks_cred_id" label="JKS credential">
              <Select
                allowClear
                options={jksCreds.map((c) => ({
                  value: c.id,
                  label: c.cred_name,
                }))}
              />
            </Form.Item>
          </Space>

          {/* You can expand more advanced fields here as needed, based on your RA form */}
        </Form>
      </Modal>

      {/* Password / download modal */}
      <Modal
        open={downloadOpen}
        title={
          downloadFormat
            ? `Password for ${downloadFormat.toUpperCase()} export`
            : "Password"
        }
        onCancel={handleDownloadCancel}
        onOk={handleDownloadConfirm}
        confirmLoading={downloadSubmitting}
        destroyOnClose
      >
        <p>
          This export includes the private key. Please enter an encryption
          password to protect the file.
        </p>
        <Input.Password
          value={downloadPassword}
          onChange={(e) => setDownloadPassword(e.target.value)}
          placeholder="Encryption password"
        />
      </Modal>
    </Space>
  );
};

const CAAutomationControl: React.FC = () => {
  return (
    <SnackbarProvider
      maxSnack={4}
      autoHideDuration={3000}
      preventDuplicate
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      style={{ zIndex: 3000, marginTop: "50px" }}
    >
      <CAAutomationControlInner />
    </SnackbarProvider>
  );
};

export default CAAutomationControl;
